import React from "react";
import { GlobalStore } from "../../store/global/GlobalStore";
import { ExchangeRate } from "./ExchangeRate";
import { isManyToOne, isOneToMany, isOneToOne } from "../../utils/quoteUtils";
import { Token } from "../../store/global/TokenEnum";
import { Amount } from "../../types/amount";
import { Tooltip } from "components/general/Tooltip";
import { FlexContainer } from "../../containers/FlexContainer";
import { FlexBetweenContainer } from "../../containers/FlexBetweenContainer";
import { DetailsLeftColumnText } from "./elements/DetailsLeftColumnText";

export interface Rate {
  from: Token;
  to: Token;
  price: Amount;
  flippedPrice: Amount;
}

export const RatesRow: React.FC = () => {
  const { theme, quote } = GlobalStore.useState(s => {
    return {
      theme: s.theme,
      quote: s.quote,
    };
  });
  const exchangeRateText =
    "Exchange rates used for this trade. Rates reflect current market prices.";
  const getExchangeRates = (): Array<Rate> => {
    if (quote) {
      if (isOneToOne(quote)) {
        return [
          {
            from: Object.keys(quote.makerTokens)[0] as Token,
            to: Object.keys(quote.takerTokens)[0] as Token,
            price: Object.values(quote.takerTokens)[0].priceBeforeFee ?? Amount.ZERO,
            flippedPrice: Object.values(quote.makerTokens)[0].priceBeforeFee ?? Amount.ZERO,
          },
        ];
      } else if (isOneToMany(quote)) {
        const makerTokens = Object.entries(quote.makerTokens);
        const takerToken = Object.keys(quote.takerTokens)[0] as Token;
        return makerTokens.map(([key, value]) => {
          const makerTokenRate = value.priceBeforeFee ?? Amount.ZERO;
          const flippedPrice: Amount = makerTokenRate.value.gt(0)
            ? new Amount("1").div(makerTokenRate)
            : Amount.ZERO;
          return {
            from: takerToken,
            to: key as Token,
            price: makerTokenRate,
            flippedPrice: flippedPrice,
          };
        });
      }
      if (isManyToOne(quote)) {
        const takerTokens = Object.entries(quote.takerTokens);
        const makerToken = Object.keys(quote.makerTokens)[0] as Token;
        const rates = takerTokens.map(([key, value]) => {
          const rate = value.priceBeforeFee ?? Amount.ZERO;
          return {
            from: key as Token,
            to: makerToken,
            price: new Amount("1").div(rate),
            flippedPrice: rate,
          };
        });
        return rates;
      }
    }

    return [];
  };
  const exchangeRates = getExchangeRates();

  return exchangeRates.map((rate, index) => (
    <FlexBetweenContainer
      data-testid="rates-row"
      key={index}
      widthExpand={true}
      justifyContent={index > 0 ? "end" : "space-between"}
    >
      {index === 0 && (
        <FlexContainer>
          <FlexContainer style={{ color: theme.font.secondary.default }}>
            <Tooltip title={exchangeRateText}>
              <DetailsLeftColumnText>
                Rate{exchangeRates.length > 1 ? "s" : ""}
              </DetailsLeftColumnText>
            </Tooltip>
          </FlexContainer>
        </FlexContainer>
      )}
      <ExchangeRate
        symbol={rate.from}
        oppositeSymbol={rate.to}
        price={rate.price}
        flippedPrice={rate.flippedPrice}
      />
    </FlexBetweenContainer>
  ));
};
